import { Dao, Element } from '@chaysehartsuff/active-html';
import { Table } from '@chaysehartsuff/active-html';
import { FieldType } from '@chaysehartsuff/active-html';
import {FormatType} from '@chaysehartsuff/active-html';
import { ContentOverlay } from '@chaysehartsuff/active-html';
import { Button, Color } from '@chaysehartsuff/active-html';
import BlogCategory from '../../models/BlogCategory.js';

export default class CategoryList extends Element {

    constructor(tableId, createBtnId) {
        super();
        
        this.table = new Table(tableId, new BlogCategory(), [
            {
                label:"ID",
                name: "id",
                sortable: true,
                searchable: false,
                visible: false,
                editable: false,
                type: FieldType.NUMBER
            },
            {
                label:"Name",
                name: "name",
                sortable: true,
                searchable: true,
                type: FieldType.STRING
            },
            {
                label:"Order",
                name: "order",
                sortable: true,
                searchable: true,
                type: FieldType.NUMBER
            },
        ]);
        this.table.addRowAction('delete', "Delete", this.onRowDelete.bind(this));
        this.table.addRowAction('edit', "Edit", this.onRowEdit.bind(this));

        this.createBtn = Element.createInstanceFromId(createBtnId);
        this.createBtn.onEvent("click", () => {
            const { formContent, nameInput, orderInput } = this.buildCategoryForm({
                title: 'New Category',
                name: '',
                order: 0,
            });

            let overlay = new ContentOverlay(
                formContent
                .addContent(
                    new Element().addClass('flex justify-end gap-3 border-t border-gray-100 pt-4')
                    .addContent(
                        new Element('button')
                        .setAttribute('type', 'button')
                        .addClass('rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-200')
                        .addContent('Cancel')
                        .onEvent('click', () => {
                            this.removeContent('new-category-overlay');
                        })
                    )
                    .addContent(
                        new Element('button')
                        .setAttribute('type', 'button')
                        .addClass('rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700')
                        .addContent('Create')
                        .onEvent('click', () => {
                            const nameValue = (nameInput.getValue() || '').trim();
                            const orderRaw = orderInput.getValue();
                            const orderValue = Number.parseInt(orderRaw, 10);

                            if (!nameValue) {
                                console.warn('Category name is required.');
                                return;
                            }

                            const category = new BlogCategory();
                            category.name = nameValue;
                            category.order = Number.isNaN(orderValue) ? 0 : orderValue;
                            category.create({}, (response) => {
                                if (response.errors && response.errors.length > 0) {
                                    console.error('Error creating category:', response.errors);
                                    return;
                                }

                                this.table.fetchRows();
                                this.removeContent('new-category-overlay');
                            });
                        })
                    )
                )
            );
            this.addContent(overlay, 'new-category-overlay');
        });
        this.bindToDom();
    }

    onRowEdit(row){
            const { formContent, nameInput, orderInput } = this.buildCategoryForm({
                title: 'Edit Category',
                name: row.name || '',
                order: row.order || 0,
            });

            let overlay = new ContentOverlay(
                formContent
                .addContent(
                    new Element().addClass('flex justify-end gap-3 border-t border-gray-100 pt-4')
                    .addContent(
                        new Element('button')
                        .setAttribute('type', 'button')
                        .addClass('rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-200')
                        .addContent('Cancel')
                        .onEvent('click', () => {
                            this.removeContent('edit-category-overlay');
                        })
                    )
                    .addContent(
                        new Element('button')
                        .setAttribute('type', 'button')
                        .addClass('rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700')
                        .addContent('Save')
                        .onEvent('click', () => {
                            const nameValue = (nameInput.getValue() || '').trim();
                            const orderRaw = orderInput.getValue();
                            const orderValue = Number.parseInt(orderRaw, 10);

                            if (!nameValue) {
                                console.warn('Category name is required.');
                                return;
                            }

                            const category = new BlogCategory();
                            category.id = row.id;
                            category.name = nameValue;
                            category.order = Number.isNaN(orderValue) ? 0 : orderValue;
                            category.update({}, (response) => {
                                if (response.errors && response.errors.length > 0) {
                                    console.error('Error saving category:', response.errors);
                                    return;
                                }

                                this.table.fetchRows();
                                this.removeContent('edit-category-overlay');
                            });
                        })
                    )
                )
            );
        this.addContent(overlay, 'edit-category-overlay');
    }

    onRowDelete(row){
        let blogCategory = new BlogCategory();
        blogCategory.id = row.id;
        blogCategory.getSync();
        Dao.modelRequest('getTotalBlogsInCategory', blogCategory, (response) => {
            let areYouSure = new ContentOverlay(
                new Element()
                    .addClass('bg-white p-10')
                    .addContent(new Element().addContent(`Are you sure you want to delete the category "${blogCategory.name}"?`).addClass('my-5 text-lg'))
                    .addContent(new Element().addContent(`This category has ${response.total} blogs assigned to it.`).addClass('mb-5 text-sm text-gray-600'))
                    .addContent(
                        new Element().addClass('flex justify-around items-center')
                            .addContent(
                                new Button('Delete', Color.RED)
                                    .onEvent('click', () => {
                                        Dao.modelRequest('deleteCategory', blogCategory, (deleteResponse) => {
                                            if(deleteResponse.errors && deleteResponse.errors.length > 0){
                                                console.error('Error deleting category:', deleteResponse.errors);
                                                return;
                                            }
                                            this.table.fetchRows();
                                         });
                                        this.removeContent(areYouSure);
                                    })
                            )
                            .addContent(
                                new Button('Cancel', Color.GRAY)
                                    .onEvent('click', () => {
                                        this.removeContent(areYouSure);
                                    })
                            )
                    )
            ).addListener(ContentOverlay.onCloseEvent, () => { this.removeContent(areYouSure); });
            this.addContent(areYouSure);
        });
    }

    buildCategoryForm({ title = 'Category', name = '', order = 0, meta_data = {} } = {}) {
        const fieldClass = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200';
        const labelClass = 'mb-1 block text-sm font-semibold text-gray-700';

        const nameInput = new Element('input')
            .setAttribute('type', 'text')
            .setAttribute('placeholder', 'Category name')
            .setAttribute('value', name)
            .addClass(fieldClass);

        const orderInput = new Element('input')
            .setAttribute('type', 'number')
            .setAttribute('min', 0)
            .setAttribute('value', String(order ?? 0))
            .addClass(fieldClass);

        const formContent = new Element()
            .addClass('w-full max-w-xl rounded-xl bg-white p-6 shadow-2xl md:p-8')
            .addContent(
                new Element().addClass('mb-6 border-b border-gray-100 pb-4 text-2xl font-bold text-gray-900').addContent(title)
            )
            .addContent(
                new Element().addClass('mb-5')
                .addContent(new Element().addClass(labelClass).addContent('Name'))
                .addContent(nameInput)
            )
            .addContent(
                new Element().addClass('mb-6')
                .addContent(new Element().addClass(labelClass).addContent('Order'))
                .addContent(orderInput)
            );

        return { formContent, nameInput, orderInput, meta_data };
    }
}